import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Mail, Phone, MapPin, Linkedin, Github, Globe, Lock, Clock, Briefcase, GraduationCap, Sparkles, DollarSign, Calendar } from 'lucide-react';

const SharedCandidateProfile = () => {
  const { token } = useParams();
  const [candidate, setCandidate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnonymized, setIsAnonymized] = useState(false);

  useEffect(() => {
    if (token) {
      fetchSharedProfile();
    }
  }, [token]);

  const fetchSharedProfile = async () => {
    try {
      // Get share link details
      const { data: shareLink, error: linkError } = await supabase
        .from('candidate_share_links')
        .select('*')
        .eq('token', token)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (linkError) throw linkError;
      
      if (!shareLink) {
        setError('This link has expired or is invalid');
        setLoading(false);
        return;
      }

      setIsAnonymized(shareLink.is_anonymized);

      // Increment view count
      await supabase
        .from('candidate_share_links')
        .update({ view_count: (shareLink.view_count || 0) + 1 })
        .eq('id', shareLink.id);

      // Fetch candidate data
      const { data: candidateData, error: candidateError } = await supabase
        .from('candidates')
        .select(`
          *,
          candidate_experience (
            *,
            companies (name)
          ),
          candidate_education (*),
          candidate_skills (
            skills (name, type)
          ),
          candidate_ai_insights (*)
        `)
        .eq('id', shareLink.candidate_id)
        .single();

      if (candidateError) throw candidateError;
      setCandidate(candidateData);
    } catch (error) {
      console.error('Error fetching shared profile:', error);
      setError('Unable to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (!candidate) return null;

  const displayName = isAnonymized 
    ? `Candidate ${candidate.id.slice(0, 8).toUpperCase()}` 
    : `${candidate.first_name} ${candidate.last_name}`;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <Card className="p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2">{displayName}</h1>
              <div className="flex flex-wrap items-center gap-3 text-muted-foreground">
                {candidate.city && !isAnonymized && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{candidate.city}</span>
                  </div>
                )}
                {candidate.availability_status && (
                  <Badge className={
                    candidate.availability_status === 'available' 
                      ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                      : candidate.availability_status === 'open_to_offers'
                      ? 'bg-blue-500/10 text-blue-700 dark:text-blue-400'
                      : 'bg-gray-500/10 text-gray-700 dark:text-gray-400'
                  }>
                    {candidate.availability_status.replace(/_/g, ' ')}
                  </Badge>
                )}
                {candidate.employment_status && (
                  <Badge variant="outline" className="capitalize">
                    {candidate.employment_status.replace(/_/g, ' ')}
                  </Badge>
                )}
              </div>
            </div>
            <Badge variant="secondary" className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Shared Profile
            </Badge>
          </div>

          {/* Salary & Availability Info */}
          {!isAnonymized && (candidate.desired_salary_min || candidate.availability_date) && (
            <div className="mt-4 flex flex-wrap gap-4 text-sm border-t pt-4">
              {candidate.desired_salary_min && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <DollarSign className="h-4 w-4" />
                  <span>
                    {candidate.desired_salary_currency || 'USD'} {candidate.desired_salary_min.toLocaleString()}
                    {candidate.desired_salary_max && ` - ${candidate.desired_salary_max.toLocaleString()}`}
                  </span>
                </div>
              )}
              {candidate.availability_date && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Available from {new Date(candidate.availability_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          )}

          {/* Contact Info - Only if not anonymized */}
          {!isAnonymized && (
            <div className="mt-4 flex flex-wrap gap-4 text-sm border-t pt-4">
              {candidate.email_primary && (
                <a href={`mailto:${candidate.email_primary}`} className="flex items-center gap-2 text-primary hover:underline">
                  <Mail className="h-4 w-4" />
                  <span>{candidate.email_primary}</span>
                </a>
              )}
              {candidate.phone_primary && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{candidate.phone_primary}</span>
                </div>
              )}
              {candidate.linkedin_url && (
                <a href={candidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
                  <Linkedin className="h-4 w-4" />
                  <span>LinkedIn</span>
                </a>
              )}
              {candidate.github_url && (
                <a href={candidate.github_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
                  <Github className="h-4 w-4" />
                  <span>GitHub</span>
                </a>
              )}
              {candidate.website_url && (
                <a href={candidate.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:underline">
                  <Globe className="h-4 w-4" />
                  <span>Website</span>
                </a>
              )}
            </div>
          )}
        </Card>

        {/* AI Summary */}
        {(candidate.ai_skills_summary || candidate.ai_products_summary) && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Summary
            </h2>
            {candidate.ai_skills_summary && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Skills Overview</h3>
                <p className="text-sm">{candidate.ai_skills_summary}</p>
              </div>
            )}
            {candidate.ai_products_summary && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Product Experience</h3>
                <p className="text-sm">{candidate.ai_products_summary}</p>
              </div>
            )}
          </Card>
        )}

        {/* Skills */}
        {candidate.candidate_skills?.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4">Skills</h2>
            <div className="flex flex-wrap gap-2">
              {candidate.candidate_skills.map((cs: any, i: number) => (
                <Badge key={i} variant="secondary">
                  {cs.skills?.name}
                </Badge>
              ))}
            </div>
          </Card>
        )}

        {/* Experience */}
        {candidate.candidate_experience?.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" />
              Experience
            </h2>
            <div className="space-y-4">
              {candidate.candidate_experience.map((exp: any) => (
                <div key={exp.id} className="border-l-2 border-primary/20 pl-4">
                  <h3 className="font-semibold">{exp.job_title}</h3>
                  <p className="text-sm text-primary">{exp.companies?.name || exp.company_name || 'Unknown Company'}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {exp.start_date} - {exp.is_current ? 'Present' : exp.end_date || 'N/A'}
                  </p>
                  {exp.description && (
                    <p className="text-sm mt-2 text-muted-foreground">{exp.description}</p>
                  )}
                  {exp.products_worked?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {exp.products_worked.map((product: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">{product}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Education */}
        {candidate.candidate_education?.length > 0 && (
          <Card className="p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              Education
            </h2>
            <div className="space-y-4">
              {candidate.candidate_education.map((edu: any) => (
                <div key={edu.id} className="border-l-2 border-primary/20 pl-4">
                  <h3 className="font-semibold">{edu.institution_name}</h3>
                  <p className="text-sm text-muted-foreground">{edu.degree}</p>
                  {edu.field_of_study && (
                    <p className="text-sm text-muted-foreground">{edu.field_of_study}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {edu.start_date} - {edu.end_date || 'Present'}
                  </p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* AI Insights */}
        {candidate.candidate_ai_insights?.[0] && (
          <Card className="p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              AI Assessment
            </h2>
            
            {candidate.candidate_ai_insights[0].summary && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Summary</h3>
                <p className="text-sm">{candidate.candidate_ai_insights[0].summary}</p>
              </div>
            )}
            
            {candidate.candidate_ai_insights[0].strengths && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Strengths</h3>
                <p className="text-sm whitespace-pre-wrap">{candidate.candidate_ai_insights[0].strengths}</p>
              </div>
            )}

            {candidate.candidate_ai_insights[0].role_fit_score && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Overall Score</h3>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold text-primary">
                    {candidate.candidate_ai_insights[0].role_fit_score}/10
                  </div>
                  <div className="flex-1 max-w-xs">
                    <div className="w-full bg-secondary h-2 rounded-full">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all" 
                        style={{ width: `${candidate.candidate_ai_insights[0].role_fit_score * 10}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>This is a shared candidate profile. Some information may be hidden for privacy.</p>
        </div>
      </div>
    </div>
  );
};

export default SharedCandidateProfile;
